/**
 * Order In Progress — family row visibility (explicit orderType, not "names imply trio").
 *
 * PROBAND_ONLY + legal father guardian uses one Biological Father row with Primary Guardian;
 * canonical.father may be populated from IMDB without meaning "parent specimen sent to lab".
 *
 * deriveOrderInProgressFamilyRows is the source of truth for the family-row
 * display model consumed by `<ComparatorList>`.
 */

import { normalizePhenotypicTrait, PhenotypicTrait } from '@/types/RequisitionFormTypes'
import type {
  CanonicalGuardianContact,
  CanonicalOrderPayload,
  CanonicalParentInfo,
  Comparator,
  FamilyMemberRelationship,
} from './canonicalOrder'
import { getParentComparator } from './comparatorReadAdapter'

/** When false, duo-* canonical orderTypes may be shown as proband-only family cards if the opposite parent has no comparator data (matches selected test with no parent samples). */
export type DeriveOrderInProgressFamilyRowsOptions = {
  parentComparatorSamplesAllowed?: boolean
}

/** FamilyCard body vs “No information entered yet.” — excludes collection-type-only (autosave defaults). */
export function canonicalParentHasComparatorCardSummaryData(p: CanonicalParentInfo | null | undefined): boolean {
  if (!p) {
    return false
  }
  const hasTextData =
    (p.parentFirstName && p.parentFirstName.trim() !== '') ||
    (p.parentLastName && p.parentLastName.trim() !== '') ||
    (p.parentDateOfBirth && p.parentDateOfBirth.trim() !== '') ||
    (p.parentMRN && p.parentMRN.trim() !== '') ||
    (p.parentPhone && p.parentPhone.trim() !== '') ||
    (p.parentEmail && p.parentEmail.trim() !== '')
  const hasSelectedPhenotypicTrait = normalizePhenotypicTrait(p.phenotypicTrait) !== PhenotypicTrait.Unknown
  const hasHPOTerms = Array.isArray(p.hpoTerms) && p.hpoTerms.length > 0
  const hasClinicalNote = Boolean(p.clinicalNote && p.clinicalNote.trim() !== '')
  return Boolean(hasTextData || hasSelectedPhenotypicTrait || hasHPOTerms || hasClinicalNote)
}

/** Any persisted comparator artifact — duo fold heuristic; includes collection type without other fields. */
export function canonicalParentHasComparatorSummaryData(p: CanonicalParentInfo | null | undefined): boolean {
  if (!p) {
    return false
  }
  if (canonicalParentHasComparatorCardSummaryData(p)) {
    return true
  }
  const hasSelectedCollectionType = p.collectionType && String(p.collectionType).trim() !== ''
  return Boolean(hasSelectedCollectionType)
}

// ── Comparator-shape parallels (Plan B / read-side migration) ────────────────
// These mirror the slot predicates above so `deriveOrderInProgressFamilyRows`
// can read from `comparators[]` directly. Slot predicates remain exported for
// the other read-side consumers (PDF renderer, summary cards) that haven't
// been migrated yet.

function comparatorHasNames(c: Comparator | null | undefined): boolean {
  const d = c?.demographic
  return Boolean(d?.firstName?.trim() || d?.lastName?.trim())
}

function comparatorHasCardSummaryData(c: Comparator | null | undefined): boolean {
  if (!c) return false
  const d = c.demographic ?? {}
  const hasTextData =
    (d.firstName && d.firstName.trim() !== '') ||
    (d.lastName && d.lastName.trim() !== '') ||
    (d.dateOfBirth && d.dateOfBirth.trim() !== '') ||
    (d.mrn && d.mrn.trim() !== '') ||
    (d.phone && d.phone.trim() !== '') ||
    (d.email && d.email.trim() !== '')
  const hasSelectedPhenotypicTrait = normalizePhenotypicTrait(c.phenotypicTrait) !== PhenotypicTrait.Unknown
  const hasHPOTerms = Array.isArray(c.hpoTerms) && c.hpoTerms.length > 0
  const hasClinicalNote = Boolean(c.clinicalNote && c.clinicalNote.trim() !== '')
  return Boolean(hasTextData || hasSelectedPhenotypicTrait || hasHPOTerms || hasClinicalNote)
}

function comparatorHasSummaryData(c: Comparator | null | undefined): boolean {
  if (!c) return false
  if (comparatorHasCardSummaryData(c)) return true
  const ct = c.specimen?.collectionType
  return Boolean(ct && String(ct).trim() !== '')
}

export type InProgressFamilyReasonCode =
  | 'PROBAND_ONLY_FATHER_GUARDIAN_OR_ECHO'
  | 'PROBAND_ONLY_MOTHER_GUARDIAN'
  | 'PROBAND_ONLY_STANDALONE_GUARDIAN'
  | 'FAMILY_ORDER_PARENT_ROWS'
  | 'EMPTY'

/** Stable strings for logs, tests, and support — extend when adding flows. */
export type OrderInProgressFamilyRowReasonCode =
  | 'ORDER_TYPE_NOT_PROBAND_ONLY'
  | 'FAMILY_SHOW_MOTHER_SLOT'
  | 'FAMILY_SHOW_FATHER_SLOT'
  | 'MOTHER_COMPARATOR_NAMES'
  | 'FATHER_COMPARATOR_NAMES'
  | 'MOTHER_PRIMARY_GUARDIAN_FROM_CANONICAL'
  | 'FATHER_PRIMARY_GUARDIAN_FROM_CANONICAL'
  | 'PROBAND_ONLY'
  | 'PROBAND_FATHER_ROW'
  | 'PROBAND_MOTHER_ROW'
  | 'PROBAND_STANDALONE_GUARDIAN_CARD'
  /** PROBAND_ONLY with no parent rows and no guardian data: placeholder card so user can add guardian post-submit */
  | 'PROBAND_GUARDIAN_PLACEHOLDER_NO_DATA'
  | 'GUARDIAN_PATH_FATHER_OR_PCM_BIO_FATHER'
  | 'GUARDIAN_PATH_MOTHER_OR_PCM_BIO_MOTHER'
  | 'FATHER_NAME_ECHO_WITHOUT_GUARDIAN_PATH'
  | 'PRIMARY_GUARDIAN_BADGE_PROBAND_PARENT_ROW'
  | 'NON_PARENT_GUARDIAN_SUPPRESSES_FATHER_NAME_ECHO'
  /** FAMILY_TRIO: badge from trio rules when canonical flags missing on reload */
  | 'TRIO_PRIMARY_GUARDIAN_RESOLVED'

export type OrderInProgressFamilyRow =
  | {
      kind: 'GUARDIAN_CONTACT'
      /** Proband-only: signing / primary guardian when they are not the biological mother or father row */
      showPrimaryGuardianBadge: boolean
      reasonCodes: readonly OrderInProgressFamilyRowReasonCode[]
    }
  | {
      kind: 'BIOLOGICAL_MOTHER'
      showPrimaryGuardianBadge: boolean
      /** When true, comparator "Sent to lab" is allowed once content exists */
      treatedAsTestedComparator: boolean
      /** Proband-only guardian lane: content rules use guardian contact, not comparator names */
      rowNonTestSubject: boolean
      reasonCodes: readonly OrderInProgressFamilyRowReasonCode[]
    }
  | {
      kind: 'BIOLOGICAL_FATHER'
      showPrimaryGuardianBadge: boolean
      treatedAsTestedComparator: boolean
      rowNonTestSubject: boolean
      reasonCodes: readonly OrderInProgressFamilyRowReasonCode[]
    }

export type OrderInProgressFamilyDisplay = {
  /** Bump when row schema or semantics change (for support payloads) */
  displaySchemaVersion: '1'
  orderType: string
  reasonCode: InProgressFamilyReasonCode
  rows: readonly OrderInProgressFamilyRow[]
}

export function guardianHasMeaningfulContact(g: CanonicalGuardianContact | null | undefined): boolean {
  if (!g) return false
  if (g.contactNotAvailable === true) return true
  return [g.firstName, g.lastName, g.email, g.phone, g.relationshipToPatient].some(
    (v) => typeof v === 'string' && v.trim() !== ''
  )
}

function pcmRelationship(co: CanonicalOrderPayload): string {
  return (co.primaryConsent?.familyMember?.relationship ?? '').trim()
}

/**
 * FAMILY_TRIO primary-guardian badges for in-progress UI. Matches session
 * hydration: when either parent comparator has `isPrimaryGuardian: true`, the
 * single-primary rule keeps the LAST flagged parent (iteration is mother
 * before father, so this preserves the "father wins both" trio contract).
 * When neither is flagged, prefer a sole guardian/PCM bio-parent path; else
 * default to the FIRST parent comparator (mother).
 *
 * Reads `isPrimaryGuardian` from the canonical MOTHER / FATHER comparators
 * directly (P5: comparator-list rule, no `trioPrimaryGuardianFromParentFlags`
 * shim).
 */
function trioPrimaryGuardianBadgesForDisplay(
  co: CanonicalOrderPayload,
  motherComp: Comparator | undefined,
  fatherComp: Comparator | undefined
): {
  mother: boolean
  father: boolean
} {
  const motherTrue = motherComp?.isPrimaryGuardian === true
  const fatherTrue = fatherComp?.isPrimaryGuardian === true
  if (motherTrue || fatherTrue) {
    // Last flagged wins; iteration order is [mother, father].
    return { mother: motherTrue && !fatherTrue, father: fatherTrue }
  }
  const gc = co.guardianContact
  const gcRel = (gc?.relationshipToPatient ?? '').toUpperCase()
  const pcmRel = pcmRelationship(co).toLowerCase()
  const fatherGuardianPath = gcRel === 'FATHER' || pcmRel === 'bio_father'
  const motherGuardianPath = gcRel === 'MOTHER' || pcmRel === 'bio_mother'
  if (fatherGuardianPath && !motherGuardianPath) {
    return { mother: false, father: true }
  }
  if (motherGuardianPath && !fatherGuardianPath) {
    return { mother: true, father: false }
  }
  // Neither flagged and no guardian/PCM path: first parent (mother) defaults.
  return { mother: true, father: false }
}

export function deriveOrderInProgressFamilyRows(
  co: CanonicalOrderPayload | null | undefined,
  options?: DeriveOrderInProgressFamilyRowsOptions
): OrderInProgressFamilyDisplay {
  if (!co) {
    return {
      displaySchemaVersion: '1',
      orderType: 'PROBAND_ONLY',
      reasonCode: 'EMPTY',
      rows: [],
    }
  }

  // Plan B: resolve canonical MOTHER / FATHER comparators once; all
  // subsequent reads (predicates, PG flag) use the comparator shape so
  // `co.mother` / `co.father` is no longer touched directly by this
  // derivation. `resolveParentForDerivation` falls back to a synthesized
  // view of the legacy slot when no canonical comparator is present, so
  // callers that haven't run the full read pipeline still get correct
  // rows.
  const motherComp = getParentComparator(co, 'mother')
  const fatherComp = getParentComparator(co, 'father')

  let orderType = (co.orderType ?? 'PROBAND_ONLY').trim() || 'PROBAND_ONLY'
  /**
   * Persisted IMDB orderType can be FAMILY_DUO_* while the selected test does not include
   * comparator samples. In that case we may fold the UI to PROBAND_ONLY for copy/stale cleanup.
   *
   * For FAMILY_DUO_MOTHER: check the *mother* comparator — the opposite (father) parent
   * is often empty in a true duo and must not force a fold, or the mother row vanishes in-progress.
   * For FAMILY_DUO_FATHER: keep the existing heuristic (empty mother) per backward-compat tests.
   */
  if (options?.parentComparatorSamplesAllowed === false) {
    if (orderType === 'FAMILY_DUO_FATHER' && !comparatorHasSummaryData(motherComp)) {
      orderType = 'PROBAND_ONLY'
    } else if (orderType === 'FAMILY_DUO_MOTHER' && !comparatorHasSummaryData(motherComp)) {
      orderType = 'PROBAND_ONLY'
    }
  }
  const gc = co.guardianContact
  const gOk = guardianHasMeaningfulContact(gc)
  const gcRel = (gc?.relationshipToPatient ?? '').toUpperCase()
  const pcmRel = pcmRelationship(co)

  const legalGuardianSelected = gcRel === 'LEGAL_GUARDIAN'
  const fatherGuardianPath = gcRel === 'FATHER' || pcmRel === 'bio_father'
  const motherGuardianPath = gcRel === 'MOTHER' || pcmRel === 'bio_mother'

  const duoMother = orderType === 'FAMILY_TRIO' || orderType === 'FAMILY_DUO_MOTHER'
  const duoFather = orderType === 'FAMILY_TRIO' || orderType === 'FAMILY_DUO_FATHER'

  if (orderType !== 'PROBAND_ONLY') {
    const mNames = comparatorHasNames(motherComp)
    const fNames = comparatorHasNames(fatherComp)
    const rows: OrderInProgressFamilyRow[] = []
    const isTrio = orderType === 'FAMILY_TRIO'
    const trioPg = isTrio ? trioPrimaryGuardianBadgesForDisplay(co, motherComp, fatherComp) : null

    // Pre-compute whether the mother/father parent rows will own the PG badge.
    const motherPrimaryFromCanonical = motherComp?.isPrimaryGuardian === true
    const fatherPrimaryFromCanonical = fatherComp?.isPrimaryGuardian === true
    const motherWillBePG =
      duoMother && (isTrio ? Boolean(trioPg?.mother) : motherPrimaryFromCanonical || motherGuardianPath)
    const fatherWillBePG =
      duoFather && (isTrio ? Boolean(trioPg?.father) : fatherPrimaryFromCanonical || fatherGuardianPath)

    /**
     * GEN-2999: family orders never render a standalone Guardian Contact card.
     * Guardianship is owned by the comparator parents (PG badge); a non-parent
     * legal guardian must be added as a non-testing comparator with the primary
     * guardian flag. The standalone card is proband-only. Any canonical
     * `guardianContact` data already on the payload is preserved, just not
     * surfaced as its own card on family orders.
     */

    if (duoMother) {
      const primaryFromCanonical = motherPrimaryFromCanonical
      /** TRIO: resolved flags (default mother) when reload omits isPrimaryGuardian. Duo: canonical + guardian path */
      const showPrimaryGuardianBadge = motherWillBePG
      const trioResolvedMother = isTrio && showPrimaryGuardianBadge && !primaryFromCanonical && !motherGuardianPath
      rows.push({
        kind: 'BIOLOGICAL_MOTHER',
        showPrimaryGuardianBadge,
        treatedAsTestedComparator: duoMother && mNames,
        rowNonTestSubject: false,
        reasonCodes: [
          'ORDER_TYPE_NOT_PROBAND_ONLY',
          'FAMILY_SHOW_MOTHER_SLOT',
          ...(mNames ? (['MOTHER_COMPARATOR_NAMES'] as const) : []),
          ...(primaryFromCanonical ? (['MOTHER_PRIMARY_GUARDIAN_FROM_CANONICAL'] as const) : []),
          ...(trioResolvedMother ? (['TRIO_PRIMARY_GUARDIAN_RESOLVED'] as const) : []),
          ...(!isTrio && showPrimaryGuardianBadge && !primaryFromCanonical
            ? (['GUARDIAN_PATH_MOTHER_OR_PCM_BIO_MOTHER'] as const)
            : []),
          ...(isTrio && showPrimaryGuardianBadge && !primaryFromCanonical && motherGuardianPath
            ? (['GUARDIAN_PATH_MOTHER_OR_PCM_BIO_MOTHER'] as const)
            : []),
        ],
      })
    }

    if (duoFather) {
      const primaryFromCanonical = fatherPrimaryFromCanonical
      const showPrimaryGuardianBadge = fatherWillBePG
      const trioResolvedFather = isTrio && showPrimaryGuardianBadge && !primaryFromCanonical && !fatherGuardianPath
      rows.push({
        kind: 'BIOLOGICAL_FATHER',
        showPrimaryGuardianBadge,
        treatedAsTestedComparator: duoFather && fNames,
        rowNonTestSubject: false,
        reasonCodes: [
          'ORDER_TYPE_NOT_PROBAND_ONLY',
          'FAMILY_SHOW_FATHER_SLOT',
          ...(fNames ? (['FATHER_COMPARATOR_NAMES'] as const) : []),
          ...(primaryFromCanonical ? (['FATHER_PRIMARY_GUARDIAN_FROM_CANONICAL'] as const) : []),
          ...(trioResolvedFather ? (['TRIO_PRIMARY_GUARDIAN_RESOLVED'] as const) : []),
          ...(!isTrio && showPrimaryGuardianBadge && !primaryFromCanonical
            ? (['GUARDIAN_PATH_FATHER_OR_PCM_BIO_FATHER'] as const)
            : []),
          ...(isTrio && showPrimaryGuardianBadge && !primaryFromCanonical && fatherGuardianPath
            ? (['GUARDIAN_PATH_FATHER_OR_PCM_BIO_FATHER'] as const)
            : []),
        ],
      })
    }

    return {
      displaySchemaVersion: '1',
      orderType,
      reasonCode: 'FAMILY_ORDER_PARENT_ROWS',
      rows,
    }
  }

  const fatherEcho = comparatorHasNames(fatherComp)
  /** Meaningful guardian who is not identified as biological mother/father (GC + PCM). */
  const nonParentGuardianPrimary = gOk && !fatherGuardianPath && !motherGuardianPath
  /** Do not show a Biological Father card from IMDB name echo when the real primary guardian is someone else */
  /** Legal guardian is never a bio-father comparator row; suppress IMDB father echo in that case. */
  const probandFatherRow =
    !legalGuardianSelected && ((gOk && fatherGuardianPath) || (fatherEcho && !nonParentGuardianPrimary))
  const probandMotherRow = gOk && motherGuardianPath

  const standaloneGuardian = nonParentGuardianPrimary
  /**
   * Show an empty Guardian Contact placeholder when no biological parent row represents the guardian
   * and no standalone guardian was filled in. This matches OrderStart (which always renders the
   * GuardianContactCard for proband-only drafts) so users can add guardian info after submit.
   */
  const guardianPlaceholder = !standaloneGuardian && !probandFatherRow && !probandMotherRow

  let reason: InProgressFamilyReasonCode = 'PROBAND_ONLY_STANDALONE_GUARDIAN'
  if (probandFatherRow) {
    reason = 'PROBAND_ONLY_FATHER_GUARDIAN_OR_ECHO'
  } else if (probandMotherRow) {
    reason = 'PROBAND_ONLY_MOTHER_GUARDIAN'
  }

  const rows: OrderInProgressFamilyRow[] = []

  if (standaloneGuardian) {
    const codes: OrderInProgressFamilyRowReasonCode[] = ['PROBAND_ONLY', 'PROBAND_STANDALONE_GUARDIAN_CARD']
    if (nonParentGuardianPrimary && fatherEcho) {
      codes.push('NON_PARENT_GUARDIAN_SUPPRESSES_FATHER_NAME_ECHO')
    }
    rows.push({
      kind: 'GUARDIAN_CONTACT',
      showPrimaryGuardianBadge: true,
      reasonCodes: codes,
    })
  } else if (guardianPlaceholder) {
    rows.push({
      kind: 'GUARDIAN_CONTACT',
      showPrimaryGuardianBadge: false,
      reasonCodes: ['PROBAND_ONLY', 'PROBAND_GUARDIAN_PLACEHOLDER_NO_DATA'],
    })
  }

  if (probandMotherRow) {
    const codes: OrderInProgressFamilyRowReasonCode[] = [
      'PROBAND_ONLY',
      'PROBAND_MOTHER_ROW',
      'PRIMARY_GUARDIAN_BADGE_PROBAND_PARENT_ROW',
    ]
    if (motherGuardianPath) {
      codes.push('GUARDIAN_PATH_MOTHER_OR_PCM_BIO_MOTHER')
    }
    rows.push({
      kind: 'BIOLOGICAL_MOTHER',
      showPrimaryGuardianBadge: true,
      treatedAsTestedComparator: false,
      rowNonTestSubject: true,
      reasonCodes: codes,
    })
  }

  if (probandFatherRow) {
    const codes: OrderInProgressFamilyRowReasonCode[] = [
      'PROBAND_ONLY',
      'PROBAND_FATHER_ROW',
      'PRIMARY_GUARDIAN_BADGE_PROBAND_PARENT_ROW',
    ]
    if (fatherGuardianPath) {
      codes.push('GUARDIAN_PATH_FATHER_OR_PCM_BIO_FATHER')
    }
    if (fatherEcho && !fatherGuardianPath) {
      codes.push('FATHER_NAME_ECHO_WITHOUT_GUARDIAN_PATH')
    }
    rows.push({
      kind: 'BIOLOGICAL_FATHER',
      showPrimaryGuardianBadge: true,
      treatedAsTestedComparator: false,
      rowNonTestSubject: true,
      reasonCodes: codes,
    })
  }

  return {
    displaySchemaVersion: '1',
    orderType,
    reasonCode: reason,
    rows,
  }
}

/**
 * ``OrderInProgress`` passes ``parentComparatorSamplesAllowed`` into
 * :func:`deriveOrderInProgressFamilyRows`. Duo ``orderType`` from IMDB can be set while the
 * selected test is proband-only (``parentSamplesRequired === 0``), so the selected parent
 * relationships carry no slots — we must still allow the proband-only fold instead of showing
 * empty comparator cards. Takes the selected-comparator relationship list (MOTHER/FATHER subset)
 * rather than the retired ``biologicalMother`` / ``biologicalFather`` boolean pair.
 */
export function parentComparatorSamplesAllowedForFamilyRowDerivation(
  orderType: string | undefined,
  comparatorSamplesAllowed: boolean,
  selectedParentRelationships: readonly FamilyMemberRelationship[]
): boolean {
  const ot = (orderType ?? 'PROBAND_ONLY').trim()
  if (ot === 'FAMILY_TRIO') {
    return comparatorSamplesAllowed
  }
  if (ot === 'FAMILY_DUO_MOTHER' || ot === 'FAMILY_DUO_FATHER') {
    const wantsComparatorSlot =
      selectedParentRelationships.includes('MOTHER') || selectedParentRelationships.includes('FATHER')
    return comparatorSamplesAllowed && wantsComparatorSlot
  }
  return comparatorSamplesAllowed
}

/** PHI-safe payload for console / support (kinds, flags, reason codes only). */
export function familyDisplayDiagnostics(d: OrderInProgressFamilyDisplay) {
  return {
    displaySchemaVersion: d.displaySchemaVersion,
    orderType: d.orderType,
    reasonCode: d.reasonCode,
    rows: d.rows.map((r) =>
      r.kind === 'GUARDIAN_CONTACT'
        ? {
            kind: r.kind,
            showPrimaryGuardianBadge: r.showPrimaryGuardianBadge,
            reasonCodes: [...r.reasonCodes],
          }
        : {
            kind: r.kind,
            reasonCodes: [...r.reasonCodes],
            showPrimaryGuardianBadge: r.showPrimaryGuardianBadge,
            treatedAsTestedComparator: r.treatedAsTestedComparator,
            rowNonTestSubject: r.rowNonTestSubject,
          }
    ),
  }
}
